/**
 * Clinical photo timelines (#18638) — `/api/v1/clinical-photos`.
 *
 * Replaces the before/after pair API. A course of treatment is a series of dated milestones, and
 * the comparison a clinician wants is any two of them, not the first and the last.
 */
import { apiClient } from "./client";

export type MilestoneKind = "BEFORE" | "PROGRESS" | "AFTER";

/** Derived from the consent record, never stored on the series. */
export type MarketingConsentStatus = "NOT_ASKED" | "GRANTED" | "WITHDRAWN";

export interface PresignedUpload {
  objectKey: string;
  url: string;
  method: string;
  expiresAt: string;
}

export interface ClinicalPhoto {
  id: string;
  objectKey: string;
  viewAngle: string | null;
  position: number;
}

export interface PhotoMilestone {
  id: string;
  kind: MilestoneKind;
  takenAt: string;
  encounterId: string | null;
  patientPackageId: string | null;
  note: string | null;
  photos: ClinicalPhoto[];
}

export interface PhotoSeries {
  id: string;
  patientId: string;
  label: string | null;
  bodyArea: string | null;
  marketingConsentStatus: MarketingConsentStatus;
  marketingConsentAt: string | null;
  marketingWithdrawnAt: string | null;
  createdAt: string;
  milestones: PhotoMilestone[];
}

export const clinicalPhotosApi = {
  capability(): Promise<{ enabled: boolean; vertical: string | null }> {
    return apiClient
      .get<{ enabled: boolean; vertical: string | null }>(
        "/v1/clinical-photos/capability",
      )
      .then((r) => r.data);
  },
  listForPatient(patientId: string): Promise<PhotoSeries[]> {
    return apiClient
      .get<PhotoSeries[]>(`/v1/clinical-photos/patients/${patientId}`)
      .then((r) => r.data);
  },
  createSeries(
    patientId: string,
    body: { label?: string; bodyArea?: string },
  ): Promise<string> {
    return apiClient
      .post<string>(`/v1/clinical-photos/patients/${patientId}`, body)
      .then((r) => r.data);
  },
  addMilestone(
    seriesId: string,
    body: {
      kind: MilestoneKind;
      takenAt?: string;
      encounterId?: string | null;
      note?: string;
    },
  ): Promise<string> {
    return apiClient
      .post<string>(`/v1/clinical-photos/series/${seriesId}/milestones`, body)
      .then((r) => r.data);
  },
  addPhoto(
    milestoneId: string,
    body: { objectKey: string; viewAngle?: string; position?: number },
  ): Promise<string> {
    return apiClient
      .post<string>(`/v1/clinical-photos/milestones/${milestoneId}/photos`, body)
      .then((r) => r.data);
  },
  /** Step 1 of an upload: a presigned PUT straight to storage. The image never touches the API. */
  presignPhoto(
    milestoneId: string,
    body: { fileName: string; mimeType?: string },
  ): Promise<PresignedUpload> {
    return apiClient
      .post<PresignedUpload>(
        `/v1/clinical-photos/milestones/${milestoneId}/photos/presign`,
        body,
      )
      .then((r) => r.data);
  },
  /**
   * Upload one file end to end: presign → PUT the bytes → record the key.
   *
   * <p>The row is written last on purpose — an abandoned or failed PUT leaves no photo claiming an
   * image that is not there.
   */
  async uploadPhoto(
    milestoneId: string,
    file: File,
    viewAngle?: string,
  ): Promise<string> {
    const presigned = await clinicalPhotosApi.presignPhoto(milestoneId, {
      fileName: file.name,
      mimeType: file.type || undefined,
    });
    // Plain fetch, not apiClient: a presigned URL carries its own signature and must NOT receive
    // our Authorization header — S3/MinIO rejects a signed request that also has one.
    const put = await fetch(presigned.url, {
      method: presigned.method,
      body: file,
      headers: file.type ? { "Content-Type": file.type } : undefined,
    });
    if (!put.ok) {
      throw new Error(`Tải ảnh lên thất bại (HTTP ${put.status}).`);
    }
    return clinicalPhotosApi.addPhoto(milestoneId, {
      objectKey: presigned.objectKey,
      viewAngle,
    });
  },
  /** Short-lived presigned GET so an <img> can render it. */
  photoViewUrl(photoId: string): Promise<string> {
    return apiClient
      .get<string>(`/v1/clinical-photos/photos/${photoId}/view-url`)
      .then((r) => r.data);
  },
  /** The wording reception must show before ticking the box. */
  marketingConsentText(): Promise<string> {
    return apiClient
      .get<string>("/v1/clinical-photos/marketing-consent-text")
      .then((r) => r.data);
  },
  /** Ticked = granted, cleared = withdrawn. Recorded as a staff attestation, not a patient click. */
  setConsent(
    seriesId: string,
    body: { confirmed: boolean; reason?: string },
  ): Promise<void> {
    return apiClient
      .post(`/v1/clinical-photos/series/${seriesId}/marketing-consent`, body)
      .then(() => undefined);
  },
  deleteSeries(seriesId: string): Promise<void> {
    return apiClient
      .delete(`/v1/clinical-photos/series/${seriesId}`)
      .then(() => undefined);
  },
};

export const clinicalPhotoQueryKeys = {
  capability: () => ["clinical-photos", "capability"] as const,
  forPatient: (patientId: string) =>
    ["clinical-photos", "patient", patientId] as const,
  viewUrl: (photoId: string) =>
    ["clinical-photos", "view-url", photoId] as const,
};

/**
 * Every milestone of a series, oldest first, flattened for a picker.
 *
 * The compare view lets a clinician pick ANY two — "session 3 vs session 7" is the question a
 * course of treatment actually raises, and a fixed Before-vs-After cannot express it.
 */
export function comparableMilestones(series: PhotoSeries): PhotoMilestone[] {
  return [...series.milestones].sort((a, b) =>
    a.takenAt.localeCompare(b.takenAt),
  );
}

/** The label a milestone shows in the picker: kind + date, since kinds repeat. */
export function milestoneLabel(m: PhotoMilestone): string {
  const kind =
    m.kind === "BEFORE" ? "Trước" : m.kind === "AFTER" ? "Sau" : "Tiến triển";
  const date = m.takenAt ? new Date(m.takenAt).toLocaleDateString("vi-VN") : "";
  return `${kind} — ${date}`;
}
